import React, { useState, useEffect } from "react";
import axios from "axios";
import { LuInfo } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { isExtinguisher } from "../shared/utils";
import { SimpleModal } from "../shared/SimpleModal";
import { BackButton } from "../shared/BackButton";
import { PhotoInput } from "../shared/PhotoInput";

export function AssetList({ efsmId, categoryName, categoryId, onBack }) {
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [assetForm, setAssetForm] = useState({
    asset_type: "",
    group_order: "",
    floor_level: "",
    item_number: "",
    location: "",
    notes: "",
    cylinder_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [hovered, setHovered] = useState(null);
  const [inlineEdit, setInlineEdit] = useState(null); // { assetId, field, value }
  const [inlineSaving, setInlineSaving] = useState(false);
  const emptyRow = {
    asset_type: "",
    item_number: "",
    group_order: "",
    floor_level: "",
    location: "",
    notes: "",
    cylinder_date: "",
  };
  const [newRow, setNewRow] = useState({ ...emptyRow });
  const [newRowSaving, setNewRowSaving] = useState(false);
  const [newRowError, setNewRowError] = useState("");
  const [groupOrderEdit, setGroupOrderEdit] = useState(null); // { groupKey, value, assetIds }
  const showCylinder = isExtinguisher(categoryName);
  // The column set is per-register, but only actual extinguisher types get a
  // cylinder date — fire blankets share this category and have no cylinder.
  const rowHasCylinder = (asset) => isExtinguisher(asset.asset_type_name);
  const typeIdIsExtinguisher = (id) =>
    isExtinguisher(assetTypes.find((t) => t.id === parseInt(id))?.name);

  // Asset info state
  const [allowAssetInfo, setAllowAssetInfo] = useState(false);
  const [infoFields, setInfoFields] = useState([]);
  const [infoModal, setInfoModal] = useState(null); // asset object | null
  const [infoEdits, setInfoEdits] = useState({}); // { assetId: { fieldId: value } }
  const [infoPhotoEdits, setInfoPhotoEdits] = useState({}); // { assetId: { slot: File } }
  const [infoSaving, setInfoSaving] = useState(false);

  const loadAssets = () => {
    axios
      .get(`${API}/assets/`)
      .then((res) => {
        setAssets(res.data.filter((a) => a.site_efsm === efsmId));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadAssets();
    // Fetch asset types via EFSM
    axios
      .get(`${API}/site-efsms/${efsmId}/`)
      .then((res) => {
        const cid = res.data.category;
        return axios.get(`${API}/asset-types/`).then((typesRes) => {
          setAssetTypes(typesRes.data.filter((t) => t.category === cid && t.is_active));
        });
      })
      .catch(() => {});
    // Fetch category config and info fields directly (lightweight, no nesting)
    if (categoryId) {
      axios
        .get(`${API}/asset-categories/${categoryId}/`)
        .then((catRes) => {
          const allowed = catRes.data.allow_asset_info || false;
          setAllowAssetInfo(allowed);
          if (allowed) {
            return axios
              .get(`${API}/asset-info-fields/?category=${categoryId}`)
              .then((fRes) => setInfoFields(fRes.data));
          }
        })
        .catch(() => {});
    }
  }, [efsmId, categoryId]);

  const openAdd = () => {
    setAssetForm({
      asset_type: "",
      group_order: "",
      floor_level: "",
      item_number: "",
      location: "",
      notes: "",
      cylinder_date: "",
    });
    setError("");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!assetForm.asset_type) {
      setError("Asset type is required.");
      return;
    }
    if (!assetForm.location.trim()) {
      setError("Location is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        site_efsm: efsmId,
        asset_type: parseInt(assetForm.asset_type),
        group_order: assetForm.group_order
          ? parseInt(assetForm.group_order)
          : null,
        floor_level: assetForm.floor_level,
        item_number: assetForm.item_number
          ? parseInt(assetForm.item_number)
          : null,
        location: assetForm.location,
        notes: assetForm.notes,
      };
      if (typeIdIsExtinguisher(assetForm.asset_type) && assetForm.cylinder_date) {
        payload.cylinder_date = assetForm.cylinder_date + "-01";
      }
      await axios.post(`${API}/assets/`, payload);
      setShowModal(false);
      loadAssets();
    } catch (e) {
      setError("Something went wrong.");
    }
    setSaving(false);
  };

  // Inline editing helpers
  const startInline = (assetId, field, currentValue) => {
    setInlineEdit({ assetId, field, value: currentValue ?? "" });
  };

  const commitInline = async () => {
    if (!inlineEdit) return;
    const { assetId, field, value } = inlineEdit;
    setInlineSaving(true);
    try {
      let patchValue = value;
      if (field === "item_number" || field === "group_order") {
        patchValue = value === "" ? null : parseInt(value);
      }
      if (field === "cylinder_date") {
        patchValue = value ? value + "-01" : null;
      }
      await axios.patch(`${API}/assets/${assetId}/`, { [field]: patchValue });
      setAssets((prev) => {
        const updated = prev.map((a) =>
          a.id === assetId ? { ...a, [field]: patchValue } : a,
        );
        return [...updated].sort((a, b) => {
          const go = (a.group_order ?? 9999) - (b.group_order ?? 9999);
          if (go !== 0) return go;
          const fl = (a.floor_level || "").localeCompare(b.floor_level || "");
          if (fl !== 0) return fl;
          return (a.item_number ?? 9999) - (b.item_number ?? 9999);
        });
      });
    } catch (e) {
      console.error("Inline save failed", e);
    }
    setInlineEdit(null);
    setInlineSaving(false);
  };

  const commitGroupOrder = async () => {
    if (!groupOrderEdit) return;
    const { value, assetIds } = groupOrderEdit;
    setInlineSaving(true);
    const newOrder = value === "" ? null : parseInt(value);
    try {
      await Promise.all(
        assetIds.map((id) =>
          axios.patch(`${API}/assets/${id}/`, { group_order: newOrder }),
        ),
      );
      setAssets((prev) => {
        const updated = prev.map((a) =>
          assetIds.includes(a.id) ? { ...a, group_order: newOrder } : a,
        );
        return [...updated].sort((a, b) => {
          const go = (a.group_order ?? 9999) - (b.group_order ?? 9999);
          if (go !== 0) return go;
          const fl = (a.floor_level || "").localeCompare(b.floor_level || "");
          if (fl !== 0) return fl;
          return (a.item_number ?? 9999) - (b.item_number ?? 9999);
        });
      });
    } catch (e) {
      console.error("Group order save failed", e);
    }
    setGroupOrderEdit(null);
    setInlineSaving(false);
  };

  const handleGroupOrderKey = (e) => {
    if (e.key === "Enter") commitGroupOrder();
    if (e.key === "Escape") setGroupOrderEdit(null);
  };

  const handleInlineKey = (e) => {
    if (e.key === "Enter") commitInline();
    if (e.key === "Escape") setInlineEdit(null);
  };

  const InlineCell = ({
    asset,
    field,
    display,
    type = "text",
    width = "100%",
    align = "left",
  }) => {
    const isEditing =
      inlineEdit &&
      inlineEdit.assetId === asset.id &&
      inlineEdit.field === field;
    if (isEditing) {
      if (field === "asset_type") {
        return (
          <select
            autoFocus
            value={inlineEdit.value}
            onChange={(e) =>
              setInlineEdit({ ...inlineEdit, value: e.target.value })
            }
            onBlur={commitInline}
            onKeyDown={handleInlineKey}
            style={{
              ...inputStyle,
              fontSize: "13px",
              padding: "4px 8px",
              width,
            }}
          >
            <option value="">— Select —</option>
            {assetTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        );
      }
      if (field === "cylinder_date") {
        return (
          <input
            autoFocus
            type="month"
            value={inlineEdit.value ? inlineEdit.value.substring(0, 7) : ""}
            onChange={(e) =>
              setInlineEdit({ ...inlineEdit, value: e.target.value })
            }
            onBlur={commitInline}
            onKeyDown={handleInlineKey}
            style={{
              ...inputStyle,
              fontSize: "13px",
              padding: "4px 8px",
              width,
            }}
          />
        );
      }
      return (
        <input
          autoFocus
          type={type}
          value={inlineEdit.value}
          onChange={(e) =>
            setInlineEdit({ ...inlineEdit, value: e.target.value })
          }
          onBlur={commitInline}
          onKeyDown={handleInlineKey}
          style={{
            ...inputStyle,
            fontSize: "13px",
            padding: "4px 8px",
            width,
            textAlign: align,
          }}
        />
      );
    }
    return (
      <div
        onClick={() =>
          startInline(
            asset.id,
            field,
            field === "asset_type" ? asset.asset_type : asset[field],
          )
        }
        title="Click to edit"
        style={{
          cursor: "text",
          minHeight: "28px",
          padding: "4px 6px",
          borderRadius: "4px",
          border: "1px solid transparent",
          transition: "border 0.15s, background 0.15s",
          textAlign: align,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.border = `1px solid ${colors.border}`;
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.border = "1px solid transparent";
          e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        {display || (
          <span style={{ color: "#bbb", fontStyle: "italic" }}>—</span>
        )}
      </div>
    );
  };

  const typeName = (id) => assetTypes.find((t) => t.id === id)?.name || "—";
  const formatCylinder = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
  };
  const expiryBadge = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T12:00:00");
    d.setFullYear(d.getFullYear() + 5);
    const monthsLeft = (d - new Date()) / (1000 * 60 * 60 * 24 * 30);
    const label = d.toLocaleDateString("en-AU", {
      month: "short",
      year: "numeric",
    });
    if (monthsLeft < 0)
      return (
        <span style={{ color: "#e74c3c", fontWeight: "600", fontSize: "12px" }}>
          Expired {label}
        </span>
      );
    if (monthsLeft < 3)
      return (
        <span style={{ color: "#e67e22", fontWeight: "600", fontSize: "12px" }}>
          Due {label}
        </span>
      );
    return <span style={{ color: "#27ae60", fontSize: "12px" }}>{label}</span>;
  };

  // Group assets by group_order + floor_level
  const groupMap = {};
  assets.forEach((a) => {
    const key = `${String(a.group_order ?? 9999).padStart(6, "0")}||${a.floor_level || "Unassigned"}`;
    if (!groupMap[key])
      groupMap[key] = {
        group_order: a.group_order,
        floor_level: a.floor_level || "Unassigned",
        items: [],
      };
    groupMap[key].items.push(a);
  });
  const floorGroups = Object.keys(groupMap).sort();

  const saveNewRow = async (e) => {
    if (e.key !== "Enter") return;
    if (!newRow.asset_type) {
      setNewRowError("Asset type required");
      return;
    }
    if (!newRow.location.trim()) {
      setNewRowError("Location required");
      return;
    }
    setNewRowSaving(true);
    setNewRowError("");
    try {
      const payload = {
        site_efsm: efsmId,
        asset_type: parseInt(newRow.asset_type),
        item_number: newRow.item_number ? parseInt(newRow.item_number) : null,
        group_order: newRow.group_order ? parseInt(newRow.group_order) : null,
        floor_level: newRow.floor_level,
        location: newRow.location,
        notes: newRow.notes,
      };
      if (typeIdIsExtinguisher(newRow.asset_type) && newRow.cylinder_date)
        payload.cylinder_date = newRow.cylinder_date + "-01";
      const res = await axios.post(`${API}/assets/`, payload);
      setAssets((prev) =>
        [...prev, res.data].sort((a, b) => {
          const go = (a.group_order ?? 9999) - (b.group_order ?? 9999);
          if (go !== 0) return go;
          const fl = (a.floor_level || "").localeCompare(b.floor_level || "");
          if (fl !== 0) return fl;
          return (a.item_number ?? 9999) - (b.item_number ?? 9999);
        }),
      );
      setNewRow({ ...emptyRow });
    } catch (err) {
      setNewRowError("Save failed");
    }
    setNewRowSaving(false);
  };

  const cols = showCylinder
    ? `${allowAssetInfo ? "24px " : ""}60px 2fr 1.5fr 1.5fr 1fr 1fr 36px`
    : `${allowAssetInfo ? "24px " : ""}60px 2fr 1.5fr 2fr 2fr 36px`;

  const saveAssetInfo = async (assetId) => {
    setInfoSaving(true);
    const edits = infoEdits[assetId] || {};
    const asset = assets.find((a) => a.id === assetId);
    const existingValues = asset?.info_values || [];

    const valuePromises = Object.entries(edits).map(([fieldId, value]) => {
      const existing = existingValues.find((v) => v.field === parseInt(fieldId));
      if (existing) {
        return axios.patch(`${API}/asset-info-values/${existing.id}/`, { value });
      }
      return axios.post(`${API}/asset-info-values/`, {
        asset: assetId,
        field: parseInt(fieldId),
        value,
      });
    });

    const photoEdits = infoPhotoEdits[assetId] || {};
    const photoPromises = Object.entries(photoEdits).map(([slot, file]) => {
      const formData = new FormData();
      formData.append(`info_photo_${slot}`, file);
      return axios.patch(`${API}/assets/${assetId}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    });

    try {
      await Promise.all([...valuePromises, ...photoPromises]);
      setInfoEdits((prev) => { const n = { ...prev }; delete n[assetId]; return n; });
      setInfoPhotoEdits((prev) => { const n = { ...prev }; delete n[assetId]; return n; });
      setInfoModal(null);
      loadAssets();
    } catch {}
    setInfoSaving(false);
  };

  return (
    <div>
      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <BackButton onClick={onBack} />
          <div style={styles.pageTitle}>{categoryName}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {inlineSaving && (
            <span style={{ fontSize: "12px", color: colors.blue }}>
              Saving...
            </span>
          )}
          <div style={styles.pageTitle}>Asset Register</div>
        </div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          <div style={{ ...styles.tableHeader, gridTemplateColumns: cols }}>
            {allowAssetInfo && <div />}
            <div>#</div>
            <div>Asset Type</div>
            <div>Floor / Group</div>
            <div>Location</div>
            {showCylinder && <div>Cylinder Date</div>}
            {showCylinder && <div>5yr Expiry</div>}
            {!showCylinder && <div>Notes</div>}
            <div></div>
          </div>

          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : assets.length === 0 ? (
            <div style={styles.loading}>
              No assets yet. Use the new asset row at the bottom to get started.
            </div>
          ) : (
            floorGroups.map((key) => {
              const group = groupMap[key];
              return (
                <React.Fragment key={key}>
                  <div
                    style={{
                      backgroundColor: "#f0f4f8",
                      padding: "6px 20px",
                      borderBottom: `1px solid ${colors.border}`,
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {/* Group order badge — click to edit all assets in group */}
                    {groupOrderEdit && groupOrderEdit.groupKey === key ? (
                      <input
                        autoFocus
                        type="number"
                        min="1"
                        value={groupOrderEdit.value}
                        onChange={(e) =>
                          setGroupOrderEdit({
                            ...groupOrderEdit,
                            value: e.target.value,
                          })
                        }
                        onBlur={commitGroupOrder}
                        onKeyDown={handleGroupOrderKey}
                        style={{
                          width: "52px",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          border: `2px solid ${colors.blue}`,
                          fontSize: "13px",
                          fontWeight: "800",
                          textAlign: "center",
                          outline: "none",
                        }}
                      />
                    ) : (
                      <span
                        onClick={() =>
                          setGroupOrderEdit({
                            groupKey: key,
                            value: group.group_order ?? "",
                            assetIds: group.items.map((a) => a.id),
                          })
                        }
                        title="Click to set group order"
                        style={{
                          fontSize: "12px",
                          fontWeight: "800",
                          color: colors.darkGrey,
                          backgroundColor:
                            group.group_order != null
                              ? colors.border
                              : "#ffeeba",
                          borderRadius: "4px",
                          padding: "2px 8px",
                          minWidth: "28px",
                          textAlign: "center",
                          cursor: "pointer",
                          border: `1px dashed ${group.group_order != null ? "#bbb" : colors.orange}`,
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#d5e8f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            group.group_order != null
                              ? colors.border
                              : "#ffeeba";
                        }}
                      >
                        {group.group_order ?? "?"}
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "700",
                        color: colors.darkGrey,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {group.floor_level}
                    </span>
                    <span style={{ fontSize: "12px", color: "#999" }}>
                      — {group.items.length} asset
                      {group.items.length !== 1 ? "s" : ""}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#bbb",
                        marginLeft: "4px",
                      }}
                    >
                      click order # to reorder
                    </span>
                  </div>
                  {group.items.map((asset) => (
                    <div
                      key={asset.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: cols,
                        padding: "6px 20px",
                        borderBottom: `1px solid ${colors.border}`,
                        fontSize: "14px",
                        color: colors.text,
                        backgroundColor:
                          hovered === asset.id ? "#fafafa" : colors.white,
                        transition: "background 0.15s",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onMouseEnter={() => setHovered(asset.id)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      {/* ℹ icon — far left info button */}
                      {allowAssetInfo && (
                        <button
                          onClick={() => setInfoModal(asset)}
                          title="Asset Info"
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: (asset.info_values?.length || asset.info_photo_1_url) ? colors.blue : "#93c5fd",
                            padding: "2px", display: "flex", alignItems: "center",
                            minHeight: "auto",
                          }}
                        >
                          <LuInfo size={14} />
                        </button>
                      )}
                      {/* # — item_number inline */}
                      <InlineCell
                        asset={asset}
                        field="item_number"
                        display={asset.item_number || null}
                        type="number"
                        align="center"
                      />
                      {/* Asset Type inline dropdown */}
                      <InlineCell
                        asset={asset}
                        field="asset_type"
                        display={
                          <span style={{ fontWeight: "500" }}>
                            {typeName(asset.asset_type)}
                          </span>
                        }
                      />
                      {/* Floor / Group inline */}
                      <InlineCell
                        asset={asset}
                        field="floor_level"
                        display={
                          asset.floor_level ? (
                            <span
                              style={{
                                color: colors.blue,
                                fontWeight: "500",
                                fontSize: "13px",
                              }}
                            >
                              {asset.floor_level}
                            </span>
                          ) : null
                        }
                      />
                      {/* Location inline */}
                      <InlineCell
                        asset={asset}
                        field="location"
                        display={
                          <span style={{ fontSize: "13px", color: "#666" }}>
                            {asset.location}
                          </span>
                        }
                      />
                      {showCylinder &&
                        (rowHasCylinder(asset) ? (
                          <InlineCell
                            asset={asset}
                            field="cylinder_date"
                            display={
                              <span style={{ fontSize: "13px" }}>
                                {formatCylinder(asset.cylinder_date)}
                              </span>
                            }
                            type="month"
                          />
                        ) : (
                          <div style={{ fontSize: "13px", color: "#ccc" }}>—</div>
                        ))}
                      {showCylinder && (
                        <div>
                          {rowHasCylinder(asset) ? expiryBadge(asset.cylinder_date) : null}
                        </div>
                      )}
                      {!showCylinder && (
                        <InlineCell
                          asset={asset}
                          field="notes"
                          display={
                            <span style={{ fontSize: "13px", color: "#666" }}>
                              {asset.notes}
                            </span>
                          }
                        />
                      )}
                      {/* Delete */}
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete "${asset.asset_type_name || "this asset"}"?`,
                              )
                            ) {
                              axios
                                .delete(`${API}/assets/${asset.id}/`)
                                .then(() =>
                                  setAssets((prev) =>
                                    prev.filter((a) => a.id !== asset.id),
                                  ),
                                )
                                .catch(() => alert("Delete failed"));
                            }
                          }}
                          title="Delete asset"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#ccc",
                            fontSize: "16px",
                            padding: "2px 4px",
                            lineHeight: 1,
                            transition: "color 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = colors.red;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#ccc";
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              );
            })
          )}
          {/* ── New asset row ───────────────────────────────────────────── */}
          <div
            style={{
              backgroundColor: "#f0faf0",
              borderTop: `2px solid ${colors.green}`,
            }}
          >
            <div
              style={{
                padding: "6px 20px 2px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: colors.green,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                + New Asset
              </span>
              {newRowError && (
                <span style={{ fontSize: "11px", color: colors.red }}>
                  {newRowError}
                </span>
              )}
              {newRowSaving && (
                <span style={{ fontSize: "11px", color: colors.blue }}>
                  Saving...
                </span>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: cols,
                padding: "6px 20px 10px",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {allowAssetInfo && <div />}
              {/* # */}
              <input
                type="number"
                min="1"
                placeholder="#"
                value={newRow.item_number}
                onChange={(e) =>
                  setNewRow({ ...newRow, item_number: e.target.value })
                }
                style={{
                  ...inputStyle,
                  fontSize: "13px",
                  padding: "4px 6px",
                  textAlign: "center",
                }}
              />
              {/* Asset Type */}
              <select
                value={newRow.asset_type}
                onChange={(e) =>
                  setNewRow({ ...newRow, asset_type: e.target.value })
                }
                style={{ ...inputStyle, fontSize: "13px", padding: "4px 6px" }}
              >
                <option value="">— Asset Type —</option>
                {assetTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {/* Floor / Group */}
              <input
                placeholder="Floor / Group"
                value={newRow.floor_level}
                onChange={(e) =>
                  setNewRow({ ...newRow, floor_level: e.target.value })
                }
                style={{ ...inputStyle, fontSize: "13px", padding: "4px 6px" }}
              />
              {/* Location */}
              <input
                placeholder="Location *"
                value={newRow.location}
                onChange={(e) =>
                  setNewRow({ ...newRow, location: e.target.value })
                }
                onKeyDown={
                  !showCylinder || !typeIdIsExtinguisher(newRow.asset_type)
                    ? saveNewRow
                    : undefined
                }
                style={{ ...inputStyle, fontSize: "13px", padding: "4px 6px" }}
              />
              {showCylinder &&
                (typeIdIsExtinguisher(newRow.asset_type) ? (
                  <input
                    type="month"
                    value={newRow.cylinder_date}
                    onChange={(e) =>
                      setNewRow({ ...newRow, cylinder_date: e.target.value })
                    }
                    onKeyDown={saveNewRow}
                    style={{
                      ...inputStyle,
                      fontSize: "13px",
                      padding: "4px 6px",
                    }}
                  />
                ) : (
                  <div />
                ))}
              {showCylinder && <div />}
              {!showCylinder && (
                <input
                  placeholder="Notes"
                  value={newRow.notes}
                  onChange={(e) =>
                    setNewRow({ ...newRow, notes: e.target.value })
                  }
                  style={{
                    ...inputStyle,
                    fontSize: "13px",
                    padding: "4px 6px",
                  }}
                />
              )}
            </div>
          </div>
          <div style={styles.count}>
            {assets.length} Asset{assets.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Asset Info Modal */}
      {infoModal && (
        <SimpleModal
          title={`Asset Info — ${typeName(infoModal.asset_type) || "Asset"}`}
          onClose={() => {
            setInfoModal(null);
            setInfoEdits((prev) => { const n = { ...prev }; delete n[infoModal.id]; return n; });
            setInfoPhotoEdits((prev) => { const n = { ...prev }; delete n[infoModal.id]; return n; });
          }}
          onSave={() => saveAssetInfo(infoModal.id)}
          saving={infoSaving}
          wide
        >
          {infoFields.length === 0 ? (
            <p style={{ color: "#94a3b8", fontStyle: "italic", margin: "0 0 12px" }}>
              No info fields defined. Add fields in Settings → Asset Info Fields.
            </p>
          ) : (
            infoFields.map((field) => {
              const existing = (infoModal.info_values || []).find((v) => v.field === field.id);
              const val = infoEdits[infoModal.id]?.[field.id] !== undefined
                ? infoEdits[infoModal.id][field.id]
                : existing?.value || "";
              return (
                <div key={field.id} style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, display: "block", marginBottom: "4px" }}>
                    {field.label}
                  </label>
                  <input
                    value={val}
                    onChange={(e) =>
                      setInfoEdits((prev) => ({
                        ...prev,
                        [infoModal.id]: { ...(prev[infoModal.id] || {}), [field.id]: e.target.value },
                      }))
                    }
                    placeholder={`Enter ${field.label.toLowerCase()}…`}
                    style={{ ...inputStyle, width: "100%" }}
                  />
                </div>
              );
            })
          )}
          <div style={{ display: "flex", gap: "20px", marginTop: "8px", flexWrap: "wrap" }}>
            {[1, 2].map((slot) => {
              const existingUrl = slot === 1 ? infoModal.info_photo_1_url : infoModal.info_photo_2_url;
              const pendingFile = infoPhotoEdits[infoModal.id]?.[slot];
              const previewUrl = pendingFile ? URL.createObjectURL(pendingFile) : existingUrl;
              return (
                <div key={slot}>
                  <div style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, marginBottom: "6px" }}>
                    Photo {slot}
                  </div>
                  {previewUrl ? (
                    <img src={previewUrl} alt={`info ${slot}`} style={{
                      width: "100px", height: "100px", objectFit: "cover",
                      borderRadius: "8px", border: `1px solid ${colors.border}`,
                      display: "block", marginBottom: "6px",
                    }} />
                  ) : (
                    <div style={{
                      width: "100px", height: "100px", borderRadius: "8px",
                      border: `2px dashed ${colors.border}`, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      color: "#bbb", fontSize: "12px", marginBottom: "6px",
                    }}>
                      No photo
                    </div>
                  )}
                  <PhotoInput
                    compact
                    takeLabel={previewUrl ? "Retake" : "Take photo"}
                    libraryLabel={previewUrl ? "Replace" : "Library"}
                    onFiles={(files) => {
                      const file = files[0];
                      if (!file) return;
                      setInfoPhotoEdits((prev) => ({
                        ...prev,
                        [infoModal.id]: { ...(prev[infoModal.id] || {}), [slot]: file },
                      }));
                    }}
                  />
                </div>
              );
            })}
          </div>
        </SimpleModal>
      )}
    </div>
  );
}
